// ============================================
// FILE: src/views/subscription/SubscriptionExpiredAdmin.js
// DESIGN SOBRE ET ÉLÉGANT
// ============================================

import React, { useState, useEffect } from 'react'
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CButton,
  CSpinner,
  CAlert,
  CBadge,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilWarning, cilCheckAlt, cilX, cilLockLocked } from '@coreui/icons'
import { stripeService } from '../../services/stripeService'
import SubscriptionPaymentModal from '../../components/stripe/SubscriptionPaymentModal'

const SubscriptionExpiredAdmin = () => {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)

  const planMetadata = {
    standard: {
      features: [
        { text: "Jusqu'à 100 membres", included: true },
        { text: 'Support email', included: true },
        { text: 'Modules essentiels', included: true },
        { text: 'Commission 2% sur cotisations', included: true },
        { text: 'Support prioritaire', included: false },
        { text: 'Personnalisation avancée', included: false },
      ],
    },
    pro: {
      isRecommended: true,
      features: [
        { text: "Jusqu'à 500 membres", included: true },
        { text: 'Support prioritaire', included: true },
        { text: 'Tous les modules', included: true },
        { text: 'Sans commission', included: true },
        { text: 'Personnalisation avancée', included: true },
        { text: 'Formation & onboarding', included: false },
      ],
    },
    premium: {
      features: [
        { text: 'Membres illimités', included: true },
        { text: 'Support 24/7 dédié', included: true },
        { text: 'Tous les modules premium', included: true },
        { text: 'Sans commission', included: true },
        { text: 'Personnalisation complète', included: true },
        { text: 'Formation & onboarding', included: true },
      ],
    },
  }

  useEffect(() => {
    loadPlans()
  }, [])

  const loadPlans = async () => {
    try {
      setLoading(true)
      const response = await stripeService.getAvailablePlans()

      const enrichedPlans = (response.plans || []).map((plan) => {
        const planId =
          plan.product.metadata?.plan_id || plan.product.name.toLowerCase().replace(/\s+/g, '_')

        return {
          ...plan,
          planId,
          metadata: planMetadata[planId] || planMetadata.standard,
        }
      })

      setPlans(enrichedPlans)
    } catch (err) {
      console.error('Erreur chargement plans:', err)
      setError('Impossible de charger les plans disponibles')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectPlan = (plan) => {
    setSelectedPlan({
      id: plan.id,
      name: plan.product.name,
      description: plan.product.description,
      price: (plan.unit_amount / 100).toFixed(2),
      interval: plan.recurring.interval === 'month' ? 'mois' : 'an',
    })
    setShowModal(true)
  }

  const handlePaymentSuccess = () => {
    setShowModal(false)
    setSelectedPlan(null)
    window.location.href = '/subscription/success'
  }

  if (loading) {
    return (
      <CContainer
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="text-center">
          <CSpinner color="primary" size="lg" />
          <p className="mt-3 text-medium-emphasis">Chargement des plans...</p>
        </div>
      </CContainer>
    )
  }

  return (
    <>
      <style>{`
        .subscription-page {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding: 60px 0;
        }
        
        .alert-banner {
          background: white;
          border-left: 4px solid #f59e0b;
          border-radius: 8px;
          padding: 20px 24px;
          margin-bottom: 48px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .page-title {
          font-size: 32px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 12px;
          text-align: center;
        }
        
        .page-subtitle {
          font-size: 16px;
          color: #6b7280;
          text-align: center;
          margin-bottom: 48px;
        }
        
        .plan-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          transition: all 0.2s ease;
          height: 100%;
          position: relative;
        }
        
        .plan-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
        }
        
        .plan-card.recommended {
          border-color: #10b981;
          border-width: 2px;
        }
        
        .recommended-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: #10b981;
          color: white;
          padding: 4px 16px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
        }
        
        .plan-header {
          padding: 32px 24px 24px;
          text-align: center;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .plan-name {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 16px;
        }
        
        .plan-price {
          font-size: 48px;
          font-weight: 700;
          color: #1f2937;
          line-height: 1;
        }
        
        .plan-currency {
          font-size: 24px;
          vertical-align: super;
        }
        
        .plan-interval {
          font-size: 16px;
          color: #6b7280;
          margin-top: 8px;
        }
        
        .plan-features {
          padding: 24px;
        }
        
        .plan-feature {
          display: flex;
          align-items: flex-start;
          margin-bottom: 12px;
          font-size: 14px;
        }
        
        .feature-icon {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          margin-right: 12px;
          margin-top: 2px;
        }
        
        .plan-feature.included {
          color: #1f2937;
        }
        
        .plan-feature.not-included {
          color: #9ca3af;
        }
        
        .plan-cta {
          padding: 0 24px 24px;
        }
        
        .select-button {
          border-radius: 8px;
          padding: 12px 24px;
          font-weight: 600;
        }
        
        .footer-text {
          text-align: center;
          color: #6b7280;
          margin-top: 48px;
        }
      `}</style>

      <div className="subscription-page">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol lg={10}>
              {/* Alerte */}
              <div className="alert-banner">
                <div className="d-flex align-items-center">
                  <CIcon icon={cilWarning} size="xl" className="text-warning me-3" />
                  <div>
                    <h5 className="mb-1 fw-bold">Abonnement expiré</h5>
                    <p className="mb-0 text-muted">
                      Votre abonnement a expiré. Sélectionnez un plan pour continuer.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <CAlert color="danger" className="mb-4">
                  {error}
                </CAlert>
              )}

              <h2 className="page-title">Choisissez votre plan</h2>
              <p className="page-subtitle">Sélectionnez le plan qui correspond à vos besoins</p>

              {plans.length === 0 ? (
                <CAlert color="info">Aucun plan disponible. Contactez le support.</CAlert>
              ) : (
                <CRow className="g-4">
                  {plans.map((plan) => {
                    const metadata = plan.metadata

                    return (
                      <CCol key={plan.id} lg={4} md={6}>
                        <div className={`plan-card ${metadata.isRecommended ? 'recommended' : ''}`}>
                          {metadata.isRecommended && (
                            <div className="recommended-badge">Recommandé</div>
                          )}

                          <div className="plan-header">
                            <div className="plan-name">{plan.product.name}</div>
                            <div className="plan-price">
                              <span className="plan-currency">€</span>
                              {(plan.unit_amount / 100).toFixed(0)}
                            </div>
                            <div className="plan-interval">
                              par {plan.recurring.interval === 'month' ? 'mois' : 'an'}
                            </div>
                          </div>

                          <div className="plan-features">
                            {metadata.features.map((feature, idx) => (
                              <div
                                key={idx}
                                className={`plan-feature ${feature.included ? 'included' : 'not-included'}`}
                              >
                                <CIcon
                                  icon={feature.included ? cilCheckAlt : cilX}
                                  className="feature-icon"
                                  style={{ color: feature.included ? '#10b981' : '#d1d5db' }}
                                />
                                <span>{feature.text}</span>
                              </div>
                            ))}
                          </div>

                          <div className="plan-cta">
                            <CButton
                              color={metadata.isRecommended ? 'success' : 'primary'}
                              className="w-100 select-button"
                              onClick={() => handleSelectPlan(plan)}
                            >
                              Choisir
                            </CButton>
                          </div>
                        </div>
                      </CCol>
                    )
                  })}
                </CRow>
              )}

              <div className="footer-text">
                <p className="mb-1">
                  <CIcon icon={cilLockLocked} className="me-2" />
                  Paiement sécurisé par Stripe
                </p>
                <p className="small mb-0">Questions ? Contactez notre support</p>
              </div>
            </CCol>
          </CRow>
        </CContainer>
      </div>

      <SubscriptionPaymentModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        plan={selectedPlan}
        onSuccess={handlePaymentSuccess}
      />
    </>
  )
}

export default SubscriptionExpiredAdmin
